import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getOnboardingProgress } from "@/lib/api";
import { CheckCircle2, Circle, ArrowRight, Tag, Boxes, Sparkles, X } from "lucide-react";

/**
 * Post-registration checklist. Shows the 3 steps a new store owner must complete:
 *   a) Poner precios · b) Capturar inventario inicial · c) Encender Chalán
 * Auto-hides when all three are done. Owner can also dismiss manually.
 */
const DISMISS_KEY = "chalann_onboarding_dismissed_v1";

export default function OnboardingChecklist() {
  const nav = useNavigate();
  const [progress, setProgress] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    let mounted = true;
    const load = () => getOnboardingProgress().then((r) => mounted && setProgress(r)).catch(() => {});
    load();
    const iv = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("chalann:toggle-changed", load);
    return () => { mounted = false; clearInterval(iv); window.removeEventListener("focus", onFocus); window.removeEventListener("chalann:toggle-changed", load); };
  }, []);

  if (!progress) return null;
  const allDone = progress.step_catalog_done && progress.step_pricing_done && progress.step_stock_done && progress.chalann_enabled;
  if (dismissed || allDone) return null;

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); };

  const steps = [
    {
      key: "prices",
      done: progress.step_pricing_done,
      icon: Tag,
      title: "Ponle precio a tus productos",
      body: "Ya te precargamos catálogo. Ahora abre la captura de precios y escribe cuánto cobra tu tienda por cada uno. Es paso a paso, con el teclado. Toca Enter para brincar al siguiente y no olvides tocar Guardar antes de salir.",
      cta: "Poner precios",
      go: () => nav("/precios-iniciales"),
      pending: Math.max(0, progress.total_products - progress.products_priced),
      testid: "onboarding-step-prices",
    },
    {
      key: "stock",
      done: progress.step_stock_done,
      icon: Boxes,
      title: "Captura tu inventario inicial",
      body: "Anota cuántas piezas tienes hoy en piso, refri y bodega. De aquí en adelante Chalán descontará cada venta automáticamente y te avisará antes de quedarte sin producto.",
      cta: "Capturar existencias",
      go: () => nav("/inventario-inicial"),
      pending: Math.max(0, progress.total_products - progress.products_with_stock),
      testid: "onboarding-step-stock",
    },
    {
      key: "chalann",
      done: progress.chalann_enabled,
      icon: Sparkles,
      title: "Enciende a Chalán",
      body: "Chalán es tu ayudante de voz. Cuando esté encendido, puedes decirle \"Chalán, dame una coca y un pan\" y él arma el ticket. El botón de encender/apagar está siempre a la vista, arriba a la derecha.",
      cta: progress.chalann_enabled ? "Chalán está encendido" : "Encender Chalán",
      go: () => document.querySelector('[data-testid="chalann-toggle-btn"]')?.click(),
      testid: "onboarding-step-chalann",
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-10 relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#ccff00]/[0.03] via-transparent to-[#ff5a00]/[0.04] p-6 md:p-8 overflow-hidden"
      data-testid="onboarding-checklist"
    >
      <button
        onClick={dismiss}
        className="absolute top-4 right-4 text-white/30 hover:text-white transition"
        title="Cerrar guía de inicio"
        data-testid="onboarding-dismiss"
      >
        <X size={16} />
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-2">
            / Primeros pasos · Configura tu tienda en 3 minutos
          </p>
          <h2 className="font-satoshi text-3xl md:text-4xl tracking-tight leading-tight">
            Ya casi listo. <span className="text-[#CCFF00]">Termina estos 3 pasos</span> y a vender.
          </h2>
        </div>
        <div className="hidden md:flex flex-col items-end shrink-0">
          <span className="font-satoshi text-3xl text-white leading-none">
            {doneCount}<span className="text-white/30">/3</span>
          </span>
          <span className="font-mono text-[9px] tracking-widest text-white/40 uppercase mt-1">completados</span>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {steps.map((s, idx) => {
          const Ico = s.icon;
          return (
            <div
              key={s.key}
              className={`relative flex flex-col rounded-2xl border p-5 transition ${
                s.done ? "border-[#CCFF00]/30 bg-[#CCFF00]/[0.03]" : "border-white/10 bg-white/[0.02] hover:border-white/20"
              }`}
              data-testid={s.testid}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`flex items-center gap-2 ${s.done ? "text-[#CCFF00]" : "text-white/80"}`}>
                  {s.done ? <CheckCircle2 size={18} /> : <Circle size={18} className="text-white/30" />}
                  <Ico size={16} className={s.done ? "text-[#CCFF00]" : "text-white/50"} />
                </div>
                <span className="font-mono text-[9px] tracking-widest text-white/30 uppercase">Paso {idx + 1}</span>
              </div>
              <h3 className={`font-satoshi text-lg leading-tight mb-2 ${s.done ? "text-[#CCFF00]" : "text-white"}`}>
                {s.title}
              </h3>
              <p className="text-white/60 text-[13px] leading-relaxed mb-4 flex-1">{s.body}</p>
              {s.pending > 0 && !s.done && (
                <p className="font-mono text-[10px] tracking-widest text-[#FF5A00] uppercase mb-3">
                  Faltan {s.pending} productos
                </p>
              )}
              <button
                onClick={s.go}
                disabled={s.done && s.key === "chalann"}
                className={`inline-flex items-center justify-between gap-2 px-4 py-2.5 rounded-full text-xs font-mono uppercase tracking-widest transition ${
                  s.done
                    ? "bg-[#CCFF00]/10 text-[#CCFF00] cursor-default"
                    : "bg-[#CCFF00] text-black hover:bg-white"
                }`}
                data-testid={`${s.testid}-cta`}
              >
                <span>{s.cta}</span>
                {!s.done && <ArrowRight size={14} />}
                {s.done && <CheckCircle2 size={14} />}
              </button>
            </div>
          );
        })}
      </div>
    </motion.section>
  );
}
